import React, { useRef, useState } from 'react';
import { Image, Upload, Trash2, Eye, EyeOff, RotateCcw, Lock, Unlock } from 'lucide-react';

export function UnderlayPanel({
  underlay,
  onUpdateUnderlay,
  onUpload,
  onDelete,
  isUploading = false,
  embedded = false,
}) {
  const fileInputRef = useRef(null);

  const {
    visible = true,
    opacity = 0.5,
    scale = 1,
    rotation = 0,
    position = { x: 0, y: 0 },
    pageCount = 1,
    currentPage = 0,
    locked = false, // Neu: PDF fixieren
  } = underlay || {};

  // Lokaler State für numerische Eingaben
  const [scaleInput, setScaleInput] = useState(scale.toFixed(2));

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      await onUpload(file, currentPage);
    }
    e.target.value = ''; // Reset für erneuten Upload
  };

  const handleChange = (key, value) => {
    onUpdateUnderlay?.({ ...underlay, [key]: value });
    // Sync local scale input when scale changes via slider
    if (key === 'scale') {
      setScaleInput(value.toFixed(2));
    }
  };

  // Numerische Skalierungseingabe
  const handleScaleInputChange = (e) => {
    setScaleInput(e.target.value);
  };

  const handleScaleInputBlur = () => {
    const parsed = parseFloat(scaleInput.replace(',', '.'));
    if (!isNaN(parsed) && parsed > 0) {
      const clamped = Math.max(0.01, Math.min(100, parsed));
      handleChange('scale', clamped);
      setScaleInput(clamped.toFixed(2));
    } else {
      setScaleInput(scale.toFixed(2));
    }
  };

  const handleScaleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleScaleInputBlur();
      e.target.blur();
    }
  };

  const handlePageChange = async (newPage) => {
    // Seite wechseln - benötigt erneuten Upload mit anderer Seite
    if (onUpload && underlay?.originalFile) {
      await onUpload(underlay.originalFile, newPage);
    } else {
      handleChange('currentPage', newPage);
    }
  };

  const handleReset = () => {
    onUpdateUnderlay?.({
      ...underlay,
      position: { x: 0, y: 0 },
      scale: 1,
      rotation: 0,
    });
  };

  const content = (
    <div className="p-3 space-y-4">
      {/* Upload Section */}
      <div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          onChange={handleFileSelect}
          className="hidden"
        />

        {!underlay?.imageUrl ? (
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors disabled:opacity-50"
          >
            <Upload size={20} />
            <span>{isUploading ? 'Wird hochgeladen...' : 'PDF hochladen'}</span>
          </button>
        ) : (
          <div className="space-y-3">
            {/* Sichtbarkeit und Lock Toggle */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">Sichtbar</span>
              <div className="flex gap-1">
                <button
                  onClick={() => handleChange('locked', !locked)}
                  className={`p-2 rounded ${locked ? 'bg-amber-100 text-amber-600' : 'bg-gray-100 text-gray-400'}`}
                  title={locked ? 'Fixiert - Klicken zum Entsperren' : 'Nicht fixiert - Klicken zum Fixieren'}
                >
                  {locked ? <Lock size={16} /> : <Unlock size={16} />}
                </button>
                <button
                  onClick={() => handleChange('visible', !visible)}
                  className={`p-2 rounded ${visible ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'}`}
                >
                  {visible ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
              </div>
            </div>

            {/* Deckkraft Slider */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600">Deckkraft</span>
                <span className="text-xs font-mono text-gray-500">{Math.round(opacity * 100)}%</span>
              </div>
              <input
                type="range"
                min={0.1}
                max={1}
                step={0.05}
                value={opacity}
                onChange={(e) => handleChange('opacity', parseFloat(e.target.value))}
                className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
              />
            </div>

            {/* Skalierung Slider + Numerische Eingabe */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600">Skalierung</span>
                <div className="flex items-center gap-1">
                  <input
                    type="text"
                    value={scaleInput}
                    onChange={handleScaleInputChange}
                    onBlur={handleScaleInputBlur}
                    onKeyDown={handleScaleInputKeyDown}
                    className="w-14 px-1 py-0.5 text-xs font-mono text-right border border-gray-300 rounded focus:border-blue-500 focus:outline-none"
                    title="Skalierung numerisch eingeben (z.B. 1.5 oder 0.75)"
                  />
                  <span className="text-xs text-gray-400">x</span>
                </div>
              </div>
              <input
                type="range"
                min={0.1}
                max={10}
                step={0.1}
                value={scale}
                onChange={(e) => handleChange('scale', parseFloat(e.target.value))}
                className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
              />
              <div className="flex justify-between text-xs text-gray-400">
                <span>0.1x</span>
                <span>10x</span>
              </div>
            </div>

            {/* Drehung Slider */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600">Drehung</span>
                <span className="text-xs font-mono text-gray-500">{rotation}°</span>
              </div>
              <input
                type="range"
                min={-180}
                max={180}
                step={1}
                value={rotation}
                onChange={(e) => handleChange('rotation', parseInt(e.target.value))}
                className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
              />
            </div>

            {/* Position Anzeige */}
            <div className={`text-xs px-2 py-1 rounded ${locked ? 'bg-amber-50 text-amber-700' : 'bg-gray-50 text-gray-500'}`}>
              Position: X = {(position.x / 1000).toFixed(1)}m, Y = {(position.y / 1000).toFixed(1)}m
              <div className={locked ? 'text-amber-500 mt-0.5' : 'text-gray-400 mt-0.5'}>
                {locked ? '🔒 Fixiert - nicht verschiebbar' : 'Im Select-Modus verschiebbar'}
              </div>
            </div>

            {/* Seiten-Auswahl (für Multi-Page PDFs) */}
            {pageCount > 1 && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600">Seite:</span>
                <select
                  value={currentPage}
                  onChange={(e) => handlePageChange(parseInt(e.target.value))}
                  className="flex-1 text-sm border rounded px-2 py-1"
                >
                  {Array.from({ length: pageCount }, (_, i) => (
                    <option key={i} value={i}>Seite {i + 1} von {pageCount}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Aktionen */}
            <div className="flex gap-2 pt-2 border-t">
              <button
                onClick={handleReset}
                className="flex items-center justify-center gap-1 px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded"
                title="Position, Skalierung und Drehung zurücksetzen"
              >
                <RotateCcw size={14} />
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded"
              >
                <Upload size={14} /> Ersetzen
              </button>
              <button
                onClick={onDelete}
                className="flex items-center justify-center gap-1 px-3 py-2 text-sm text-red-600 bg-red-50 hover:bg-red-100 rounded"
                title="Unterlage entfernen"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (embedded) return content;

  return (
    <div className="absolute left-4 bottom-14 w-64 bg-white rounded-lg shadow-lg z-10">
      <div className="p-3 border-b flex items-center gap-2">
        <Image size={18} className="text-gray-600" />
        <span className="font-semibold text-gray-800">Grundriss-Unterlage</span>
      </div>
      {content}
    </div>
  );
}
